/**
 * QR-Code-Modal für Empfehlungs-Links.
 *
 * - Abgerundeter Stil (rounded dots + extra-rounded Ecken)
 * - Farbe wählbar (Presets + freier Farbwähler)
 * - Download als PNG oder JPG
 *
 * Die Lib (qr-code-styling, ~50kb) wird lazy geladen — erst beim ersten
 * Öffnen des Modals, damit sie nicht im Haupt-Bundle landet.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Loader2, X } from "lucide-react";
import type QRCodeStyling from "qr-code-styling";

const PRESET_COLORS = [
  { hex: "#1a1a1a", label: "Schwarz" },
  { hex: "#e8630a", label: "DP-Orange" },
  { hex: "#0e9f6e", label: "Grün" },
  { hex: "#2563eb", label: "Blau" },
  { hex: "#9333ea", label: "Lila" },
  { hex: "#dc2626", label: "Rot" },
];

function styleOptions(color: string) {
  return {
    dotsOptions: { color, type: "rounded" as const },
    cornersSquareOptions: { color, type: "extra-rounded" as const },
    cornersDotOptions: { color, type: "dot" as const },
  };
}

export function QrCodeModal({
  url,
  filename = "qr-code",
  onClose,
}: {
  url: string;
  filename?: string;
  onClose: () => void;
}) {
  const [color, setColor] = useState("#1a1a1a");
  const [ready, setReady] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const qrRef = useRef<QRCodeStyling | null>(null);
  const libRef = useRef<typeof QRCodeStyling | null>(null);
  const colorRef = useRef(color);
  colorRef.current = color;

  function baseOptions(qrColor: string) {
    return {
      width: 640,
      height: 640,
      type: "canvas" as const,
      data: url,
      margin: 24,
      qrOptions: { errorCorrectionLevel: "M" as const },
      ...styleOptions(qrColor),
    };
  }

  // Lib lazy laden + QR initial rendern (Vorschau auf weißem Grund)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const QRCodeStylingLib = (await import("qr-code-styling")).default;
      if (cancelled) return;
      libRef.current = QRCodeStylingLib;
      const qr = new QRCodeStylingLib({
        ...baseOptions("#1a1a1a"),
        backgroundOptions: { color: "#ffffff" },
      });
      qrRef.current = qr;
      if (previewRef.current) {
        previewRef.current.innerHTML = "";
        qr.append(previewRef.current);
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Farbwechsel live anwenden
  useEffect(() => {
    qrRef.current?.update(styleOptions(color));
  }, [color]);

  // Download über eine frische Offscreen-Instanz:
  // PNG → transparenter Hintergrund, JPG → weiß (JPEG kann kein Alpha).
  function download(ext: "png" | "jpeg") {
    const Lib = libRef.current;
    if (!Lib) return;
    const qr = new Lib({
      ...baseOptions(colorRef.current),
      backgroundOptions: {
        color: ext === "png" ? "rgba(0,0,0,0)" : "#ffffff",
      },
    });
    qr.download({ name: filename, extension: ext });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">QR-Code für deinen Link</h3>
            <p className="mt-0.5 text-[11px] text-ink-muted font-mono break-all leading-snug">
              {url}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink shrink-0"
            aria-label="Schließen"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Vorschau — Canvas wird per CSS auf Kartenbreite skaliert */}
        <div className="rounded-2xl border border-line bg-white p-3 grid place-items-center min-h-[280px]">
          {!ready && <Loader2 className="size-5 animate-spin text-ink-muted" />}
          <div
            ref={previewRef}
            className={ready ? "w-full [&_canvas]:w-full [&_canvas]:h-auto [&_canvas]:rounded-xl" : "hidden"}
          />
        </div>

        {/* Farbe */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted mb-1.5">
            Farbe
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {PRESET_COLORS.map((c) => (
              <button
                key={c.hex}
                type="button"
                title={c.label}
                onClick={() => setColor(c.hex)}
                className={`size-7 rounded-full border-2 transition ${
                  color === c.hex ? "border-ink scale-110" : "border-transparent"
                }`}
                style={{ backgroundColor: c.hex }}
              />
            ))}
            <label
              className="size-7 rounded-full border border-dashed border-line grid place-items-center cursor-pointer text-[10px] text-ink-muted hover:border-ink"
              title="Eigene Farbe wählen"
            >
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="sr-only"
              />
              🎨
            </label>
          </div>
          <p className="mt-1 text-[10px] text-ink-muted">
            Tipp: dunkle Farbe auf weißem Grund scannt am zuverlässigsten.
          </p>
        </div>

        {/* Download */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => download("png")}
            disabled={!ready}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg brand-gradient text-white text-sm font-medium px-3 py-2 hover:brightness-110 disabled:opacity-50"
          >
            <Download className="size-4" /> PNG (transparent)
          </button>
          <button
            type="button"
            onClick={() => download("jpeg")}
            disabled={!ready}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-line bg-white text-ink-soft text-sm font-medium px-3 py-2 hover:bg-cream-soft disabled:opacity-50"
          >
            <Download className="size-4" /> JPG (weiß)
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
